import React from 'react';


const CalendarGrid = ({ view, currentDate, appointments, onAppointmentClick }) => {
  const supportTypeColors = {
    strategy: 'bg-blue-100 border-blue-400 text-blue-800',
    content: 'bg-green-100 border-green-400 text-green-800',
    technical: 'bg-purple-100 border-purple-400 text-purple-800',
    training: 'bg-orange-100 border-orange-400 text-orange-800'
  };

  const supportTypeLabels = {
    strategy: 'Strategy',
    content: 'Content',
    technical: 'Technical',
    training: 'Training'
  };

  // Generate calendar days for month view
  const generateMonthDays = () => {
    const year = currentDate?.getFullYear();
    const month = currentDate?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay?.getDate();
    const startingDayOfWeek = firstDay?.getDay();

    const days = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days?.push({ isEmpty: true, key: `empty-${i}` });
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateString = date?.toISOString()?.split('T')?.[0];
      const dayAppointments = appointments?.filter(apt => apt?.appointment_date === dateString);

      days?.push({
        date,
        dateString,
        dayNumber: day,
        appointments: dayAppointments,
        isToday: dateString === new Date()?.toISOString()?.split('T')?.[0],
        key: dateString
      });
    }

    return days;
  };

  // Generate week days for week view
  const generateWeekDays = () => {
    const days = [];
    const startOfWeek = new Date(currentDate);
    const dayOfWeek = startOfWeek?.getDay();
    startOfWeek?.setDate(startOfWeek?.getDate() - dayOfWeek);

    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date?.setDate(startOfWeek?.getDate() + i);
      const dateString = date?.toISOString()?.split('T')?.[0];
      const dayAppointments = appointments?.filter(apt => apt?.appointment_date === dateString);

      days?.push({
        date,
        dateString,
        dayNumber: date?.getDate(),
        dayName: date?.toLocaleDateString('en-US', { weekday: 'short' }),
        appointments: dayAppointments,
        isToday: dateString === new Date()?.toISOString()?.split('T')?.[0],
        key: dateString
      });
    }

    return days;
  };

  const monthDays = view === 'month' ? generateMonthDays() : [];
  const weekDays = view === 'week' ? generateWeekDays() : [];

  if (view === 'month') {
    return (
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {/* Weekday Headers */}
        <div className="grid grid-cols-7 border-b border-border">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']?.map(day => (
            <div
              key={day}
              className="p-3 text-center text-sm font-semibold text-foreground bg-muted"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {monthDays?.map(day => {
            if (day?.isEmpty) {
              return (
                <div
                  key={day?.key}
                  className="min-h-32 border-r border-b border-border bg-muted/30"
                />
              );
            }

            return (
              <div
                key={day?.key}
                className={`min-h-32 border-r border-b border-border p-2 hover:bg-muted/50 transition-colors ${
                  day?.isToday ? 'bg-primary/5' : 'bg-background'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span
                    className={`text-sm font-semibold ${
                      day?.isToday
                        ? 'bg-primary text-primary-foreground w-7 h-7 rounded-full flex items-center justify-center'
                        : 'text-foreground'
                    }`}
                  >
                    {day?.dayNumber}
                  </span>
                  {day?.appointments?.length > 0 && (
                    <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                      {day?.appointments?.length}
                    </span>
                  )}
                </div>

                {/* Appointments */}
                <div className="space-y-1">
                  {day?.appointments?.slice(0, 3)?.map(apt => (
                    <button
                      key={apt?.id}
                      onClick={() => onAppointmentClick(apt)}
                      className={`w-full text-left px-2 py-1 rounded text-xs border-l-2 ${
                        supportTypeColors?.[apt?.support_type] || 'bg-gray-100 border-gray-400 text-gray-800'
                      } hover:opacity-80 transition-opacity`}
                    >
                      <div className="font-medium truncate">{apt?.appointment_time}</div>
                      <div className="truncate opacity-90">{apt?.full_name}</div>
                    </button>
                  ))}
                  {day?.appointments?.length > 3 && (
                    <div className="text-xs text-muted-foreground text-center">
                      +{day?.appointments?.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Week View
  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Weekday Headers */}
      <div className="grid grid-cols-7 border-b border-border">
        {weekDays?.map(day => (
          <div
            key={day?.key}
            className={`p-4 text-center border-r border-border last:border-r-0 ${
              day?.isToday ? 'bg-primary/10' : 'bg-muted'
            }`}
          >
            <div className="text-sm font-semibold text-muted-foreground">{day?.dayName}</div>
            <div
              className={`text-2xl font-bold mt-1 ${
                day?.isToday
                  ? 'bg-primary text-primary-foreground w-10 h-10 rounded-full flex items-center justify-center mx-auto'
                  : 'text-foreground'
              }`}
            >
              {day?.dayNumber}
            </div>
          </div>
        ))}
      </div>

      {/* Week Appointments */}
      <div className="grid grid-cols-7">
        {weekDays?.map(day => (
          <div
            key={day?.key}
            className={`min-h-96 border-r border-border last:border-r-0 p-3 ${
              day?.isToday ? 'bg-primary/5' : 'bg-background'
            }`}
          >
            {day?.appointments?.length === 0 ? (
              <div className="text-center text-muted-foreground text-sm mt-8">
                No appointments
              </div>
            ) : (
              <div className="space-y-2">
                {day?.appointments?.map(apt => (
                  <button
                    key={apt?.id}
                    onClick={() => onAppointmentClick(apt)}
                    className={`w-full text-left p-3 rounded-lg border-l-4 ${
                      supportTypeColors?.[apt?.support_type] || 'bg-gray-100 border-gray-400 text-gray-800'
                    } hover:opacity-80 transition-opacity shadow-sm`}
                  >
                    <div className="font-semibold text-sm mb-1">{apt?.appointment_time}</div>
                    <div className="text-sm font-medium mb-1">{apt?.full_name}</div>
                    <div className="text-xs opacity-90">
                      {supportTypeLabels?.[apt?.support_type] || apt?.support_type}
                    </div>
                    <div className="text-xs opacity-75 mt-1 truncate">{apt?.practice_name}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CalendarGrid;